'use client';

import { motion, useMotionValue, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Calendar, Check, Mail, Rocket, Sparkles, UserCheck } from 'lucide-react';
import Image from 'next/image';
import NextLink from 'next/link';
import { useTranslations } from 'next-intl';
import { MouseEvent, useEffect, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useLandingCTATracking } from '@/features/landing/hooks/useLandingCTATracking';
import { useSectionVisibility } from '@/features/landing/hooks/useSectionVisibility';
import { siteConfig } from '@/lib/config/site';

const COFOUNDER_KEYS = ['adrian', 'enes', 'luis'] as const;

const COFOUNDER_AVATARS = {
  adrian: '/landing/founder/adrian.jpg',
  enes: '/landing/founder/enes.jpg',
  luis: '/landing/founder/luis.jpg', // TODO: Add Luis image
} as const;

const COFOUNDER_FALLBACKS = {
  adrian: 'AD',
  enes: 'EN',
  luis: 'LU',
} as const;

export function FinalCTASection() {
  const t = useTranslations('landing.finalCTA');
  const containerRef = useRef<HTMLElement>(null);
  const { ref: visibilityRef } = useSectionVisibility('final_cta');
  const { trackCTAClick } = useLandingCTATracking();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const handleMainCTAClick = () => {
    trackCTAClick({
      ctaLocation: 'final_main',
      ctaText: t('card.cta'),
      ctaType: 'primary',
      section: 'final_cta',
      destinationUrl: siteConfig.bookingUrl,
    });
  };

  const handleEmailClick = () => {
    trackCTAClick({
      ctaLocation: 'final_email',
      ctaText: t('contact.email'),
      ctaType: 'secondary',
      section: 'final_cta',
      destinationUrl: 'mailto:help@bite-club.app',
    });
  };

  const handleCalendlyLinkClick = () => {
    trackCTAClick({
      ctaLocation: 'final_calendly_link',
      ctaText: t('contact.bookCall'),
      ctaType: 'secondary',
      section: 'final_cta',
      destinationUrl: siteConfig.bookingUrl,
    });
  };

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const outcomes = ['item1', 'item2', 'item3', 'item4'];
  const nextSteps = [
    { key: 'step1', icon: Calendar },
    { key: 'step2', icon: UserCheck },
    { key: 'step3', icon: Rocket },
  ];

  return (
    <section
      id="contact"
      ref={(el) => {
        (containerRef as React.MutableRefObject<HTMLElement | null>).current = el;
        (visibilityRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
      className="relative py-24 lg:py-32 bg-background overflow-hidden"
    >
      {/* Dynamic Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[128px] -translate-y-1/2" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-[128px] translate-y-1/2" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto space-y-16">
          {/* Header Section */}
          <motion.div style={{ opacity }} className="text-center space-y-6 max-w-3xl mx-auto">
            <Badge
              variant="outline"
              className="mb-6 px-4 py-1.5 text-sm border-primary/30 bg-primary/10 text-primary"
            >
              <Sparkles className="w-3.5 h-3.5 mr-2 text-primary" />
              {t('badge')}
            </Badge>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              {t.rich('title', {
                highlight: (chunks) => (
                  <span className="text-primary relative inline-block">
                    {chunks}
                    <svg
                      className="absolute w-full h-3 -bottom-1 left-0 text-primary/20 -z-10"
                      viewBox="0 0 100 10"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M0 5 Q 50 10 100 5"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                      />
                    </svg>
                  </span>
                ),
              })}
            </h2>

            {/* Outcomes Grid */}
            <div className="grid sm:grid-cols-2 gap-4 pt-8 text-left">
              {outcomes.map((outcome, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start space-x-3 p-4 rounded-xl bg-muted/30 border border-border/50 hover:bg-muted/50 hover:border-primary/20 transition-all duration-300 group"
                >
                  <div className="mt-1 bg-primary/10 p-1.5 rounded-full group-hover:scale-110 transition-transform duration-300">
                    <Check className="h-4 w-4 text-primary" />
                  </div>
                  <p className="text-lg text-muted-foreground group-hover:text-foreground transition-colors">
                    {t(`outcomes.${outcome}`)}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Main 3D Card Section */}
          <div className="perspective-distant">
            <TiltCard>
              <div className="relative bg-card/80 backdrop-blur-xl border border-primary/20 rounded-3xl p-8 md:p-12 shadow-2xl shadow-primary/10 overflow-hidden">
                {/* Decorative Gradients */}
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />

                <div className="relative z-10 flex flex-col items-center text-center space-y-8">
                  <div className="space-y-4 max-w-2xl">
                    <h3 className="text-3xl md:text-4xl font-bold text-foreground">
                      {t('card.title')}
                    </h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      {t('card.subtitle')}
                    </p>
                  </div>

                  <div className="flex flex-wrap justify-center gap-3">
                    <Badge
                      variant="secondary"
                      className="text-base px-4 py-1.5 bg-primary/10 text-primary hover:bg-primary/20"
                    >
                      {t('card.badge')}
                    </Badge>
                  </div>

                  <Button
                    asChild
                    size="lg"
                    className="text-lg px-10 py-7 h-auto shadow-xl shadow-primary/20 hover:scale-105 hover:shadow-primary/30 transition-all duration-300 bg-primary text-primary-foreground font-semibold rounded-full group"
                  >
                    <NextLink
                      href={siteConfig.bookingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleMainCTAClick}
                    >
                      {t('card.cta')}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </NextLink>
                  </Button>

                  {/* Next Steps Timeline */}
                  <div className="w-full pt-8 border-t border-border/50">
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-6">
                      {t('card.nextStepsTitle')}
                    </p>
                    <div className="grid md:grid-cols-3 gap-6 relative">
                      {/* Connecting Line (Desktop) */}
                      <div className="hidden md:block absolute top-6 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-transparent via-border to-transparent" />

                      {nextSteps.map((step, index) => (
                        <div
                          key={index}
                          className="relative flex flex-col items-center gap-3 group/step"
                        >
                          <div className="w-12 h-12 rounded-full bg-background border-2 border-border flex items-center justify-center z-10 group-hover/step:border-primary group-hover/step:scale-110 transition-all duration-300 shadow-sm">
                            <step.icon className="w-5 h-5 text-muted-foreground group-hover/step:text-primary transition-colors" />
                          </div>
                          <div className="text-center space-y-1">
                            <span className="block text-sm font-bold text-foreground">
                              {t(`card.nextSteps.${step.key}`)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground/80 flex items-center gap-2 bg-muted/50 px-4 py-1.5 rounded-full">
                    <Check className="w-3.5 h-3.5 text-primary" />
                    {t('card.guarantee')}
                  </p>
                </div>
              </div>
            </TiltCard>
          </div>

          {/* Founder & Contact Grid */}
          <div className="grid md:grid-cols-2 gap-8 items-stretch">
            {/* Contact Options */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-muted/30 rounded-2xl p-8 border border-border/50 flex flex-col justify-center space-y-6 hover:bg-muted/50 transition-colors"
            >
              <h4 className="text-xl font-semibold">{t('contact.title')}</h4>
              <div className="space-y-4">
                <NextLink
                  href="mailto:help@bite-club.app"
                  onClick={handleEmailClick}
                  className="flex items-center gap-4 p-3 rounded-xl bg-background border border-border hover:border-primary/30 hover:shadow-md transition-all group"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{t('contact.email')}</p>
                    <p className="text-sm text-muted-foreground">help@bite-club.app</p>
                  </div>
                </NextLink>
                <NextLink
                  href={siteConfig.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleCalendlyLinkClick}
                  className="flex items-center gap-4 p-3 rounded-xl bg-background border border-border hover:border-primary/30 hover:shadow-md transition-all group w-full"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{t('contact.bookCall')}</p>
                    <p className="text-sm text-muted-foreground">
                      {t('contact.bookCallDesc') || 'Schedule a demo'}
                    </p>
                  </div>
                </NextLink>
              </div>
            </motion.div>

            {/* Co-Founders Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 border border-border shadow-sm flex flex-col justify-center relative overflow-hidden"
            >
              <CoFounderCarousel />
            </motion.div>
          </div>

          {/* Final Footer Note */}
          <div className="text-center pt-8">
            <p className="text-sm text-muted-foreground/60 italic">{t('ps')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CoFounderCarousel() {
  const t = useTranslations('landing.finalCTA');
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoPlayInterval = 10000; // 10 seconds

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % COFOUNDER_KEYS.length);
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, []);

  const currentKey = COFOUNDER_KEYS[currentIndex];
  const currentName = t(`coFounders.${currentKey}.name`);
  const currentRole = t(`coFounders.${currentKey}.role`);
  const currentQuote = t(`coFounders.${currentKey}.quote`);

  return (
    <div className="relative">
      <div className="absolute top-0 right-0 p-4 opacity-10">
        <Sparkles className="w-24 h-24" />
      </div>

      <div className="relative z-10 space-y-6">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          <p className="text-lg md:text-xl italic text-muted-foreground leading-relaxed min-h-[120px]">
            &#34;{currentQuote}&#34;
          </p>
          <div className="flex items-center gap-4">
            <div className="relative h-14 w-14 rounded-full overflow-hidden border-2 border-primary/20 flex-shrink-0">
              <Image
                src={COFOUNDER_AVATARS[currentKey]}
                alt={currentName}
                fill
                className="object-cover"
                sizes="56px"
              />
            </div>
            <div>
              <p className="font-bold text-foreground">{currentName}</p>
              <p className="text-sm text-muted-foreground">{currentRole}</p>
            </div>
          </div>
        </motion.div>

        {/* Carousel Indicators */}
        <div className="flex justify-center gap-2 pt-4">
          {COFOUNDER_KEYS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'w-8 bg-primary'
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              aria-label={`Go to co-founder ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TiltCard({ children }: { children: React.ReactNode }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useTransform(mouseY, [-0.5, 0.5], [7, -7]);
  const rotateY = useTransform(mouseX, [-0.5, 0.5], [-7, 7]);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseXFromCenter = e.clientX - rect.left - width / 2;
    const mouseYFromCenter = e.clientY - rect.top - height / 2;

    mouseX.set(mouseXFromCenter / width);
    mouseY.set(mouseYFromCenter / height);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
      }}
      className="relative w-full transition-all duration-200 ease-out"
    >
      <div style={{ transform: 'translateZ(50px)', transformStyle: 'preserve-3d' }}>{children}</div>
    </motion.div>
  );
}
